import { z } from 'zod';

import { ContractStatusSchema } from './contracts.js';
import { OpsDepartmentSchema } from './ops-department.js';
import { TravellerDocumentRequestStatusSchema } from './traveller-documents.js';

/**
 * Operations queue of converted leads ("Fresh converted leads").
 * Server source: apps/api/src/modules/ops-cases.
 */

export const OPS_CASE_STATUSES = [
  'QUEUED',
  'ASSIGNED',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
] as const;
export const OpsCaseStatusSchema = z.enum(OPS_CASE_STATUSES);
export type OpsCaseStatus = z.infer<typeof OpsCaseStatusSchema>;

/** Screen tabs - each maps to a status filter server-side. */
export const OPS_CASE_VIEWS = ['fresh', 'unassigned', 'in_progress', 'completed', 'all'] as const;
export const OpsCaseViewSchema = z.enum(OPS_CASE_VIEWS);
export type OpsCaseView = z.infer<typeof OpsCaseViewSchema>;

/**
 * Itinerary component kinds ops books, as shown in the table. Cruise is its
 * own component kind; visa / insurance / meal are VAS components told apart
 * by which master they point at; `fixed_departure` is a fixed departure's
 * land package (2026-10-28); `other` is a VAS with none of those.
 */
export const OPS_ACTIONABLE_KINDS = [
  'flight',
  'hotel',
  'activity',
  'transfer',
  'cruise',
  'visa',
  'insurance',
  'meal',
  'fixed_departure',
  'other',
] as const;
export type OpsActionableKind = (typeof OPS_ACTIONABLE_KINDS)[number];

/**
 * What a VAS component actually is (from its FK), for labels and buckets.
 * FIXED_DEPARTURE = the synthetic "Land package" VAS a fixed departure sells.
 */
export const OPS_VAS_KINDS = ['VISA', 'INSURANCE', 'MEAL', 'FIXED_DEPARTURE'] as const;
export const OpsVasKindSchema = z.enum(OPS_VAS_KINDS);
export type OpsVasKind = z.infer<typeof OpsVasKindSchema>;

/**
 * `total` = live components of that kind; `pending` = not issued yet (held
 * ones included); `held` = on hold with the supplier, awaiting payment.
 */
export const OpsActionableCountSchema = z.object({
  total: z.number().int(),
  pending: z.number().int(),
  held: z.number().int().default(0),
});
export type OpsActionableCount = z.infer<typeof OpsActionableCountSchema>;

export const OpsActionableSchema = z.object({
  flight: OpsActionableCountSchema,
  hotel: OpsActionableCountSchema,
  activity: OpsActionableCountSchema,
  transfer: OpsActionableCountSchema,
  cruise: OpsActionableCountSchema,
  visa: OpsActionableCountSchema,
  insurance: OpsActionableCountSchema,
  meal: OpsActionableCountSchema,
  /** A fixed departure's land package. */
  fixed_departure: OpsActionableCountSchema.default({ total: 0, pending: 0, held: 0 }),
  /** VAS components that are none of the above. */
  other: OpsActionableCountSchema,
});
export type OpsActionable = z.infer<typeof OpsActionableSchema>;

/** One desk's case of a lead, as listed inside the lead's row. */
export const OpsDeskSchema = z.object({
  id: z.string().uuid(),
  department: OpsDepartmentSchema,
  status: OpsCaseStatusSchema,
  assignedTo: z.object({ id: z.string().uuid(), fullName: z.string() }).nullable(),
  assignmentSource: z.enum(['ROUND_ROBIN', 'MANUAL']).nullable(),
});
export type OpsDesk = z.infer<typeof OpsDeskSchema>;

export const OpsCaseRowSchema = z.object({
  id: z.string().uuid(),
  status: OpsCaseStatusSchema,
  /** The desk this case belongs to; its items and counts are that desk's only. */
  department: OpsDepartmentSchema.default('DMC_HOTEL'),
  /**
   * Every desk working this lead (Flight / DMC-Hotel / Visa), each with its
   * own owner. The list shows one row per lead; `id` is the first desk's case.
   * Empty outside the list (booking sheet, assign result).
   */
  desks: z.array(OpsDeskSchema).default([]),
  leadId: z.string().uuid(),
  leadReference: z.string(),
  itineraryId: z.string().uuid().nullable(),
  /** The itinerary the lead was won on (`Lead.convertedItineraryId`); null until Won. */
  wonItineraryId: z.string().uuid().nullable().default(null),
  customerName: z.string(),
  customerMobile: z.string().nullable(),
  destinationName: z.string().nullable(),
  tripTitle: z.string().nullable(),
  tripScope: z.enum(['DOMESTIC', 'INTERNATIONAL']).nullable(),
  travellerCount: z.number().int().nullable(),
  travelStartDate: z.coerce.date().nullable(),
  saleAmount: z.number().nullable(),
  salesOwnerName: z.string().nullable(),
  documentStatus: TravellerDocumentRequestStatusSchema.nullable(),
  assignedTo: z.object({ id: z.string().uuid(), fullName: z.string() }).nullable(),
  assignmentSource: z.enum(['ROUND_ROBIN', 'MANUAL']).nullable(),
  /**
   * The lead's Operations owner (OPS role, 2026-10-30): same on every desk,
   * sees the whole lead, cross-checks the executives' holds and asks for
   * fund. Null until someone holds the OPS role.
   */
  coordinator: z.object({ id: z.string().uuid(), fullName: z.string() }).nullable().default(null),
  queuedAt: z.coerce.date(),
  assignedAt: z.coerce.date().nullable(),
  firstOpenedAt: z.coerce.date().nullable(),
  completedAt: z.coerce.date().nullable(),
  actionable: OpsActionableSchema,
  /** Earliest last-payment date among this lead's held items, if any. */
  nextPaymentDue: z.coerce.date().nullable().default(null),
});
export type OpsCaseRow = z.infer<typeof OpsCaseRowSchema>;

export const OpsCaseListResponseSchema = z.object({
  data: z.array(OpsCaseRowSchema),
  pagination: z.object({
    page: z.number().int(),
    pageSize: z.number().int(),
    totalItems: z.number().int(),
    totalPages: z.number().int(),
    hasNext: z.boolean(),
    hasPrev: z.boolean(),
  }),
});
export type OpsCaseListResponse = z.infer<typeof OpsCaseListResponseSchema>;

export const OpsCaseSummarySchema = z.object({
  fresh: z.number().int(),
  unassigned: z.number().int(),
  inProgress: z.number().int(),
  completedThisMonth: z.number().int(),
  /** Held items whose last payment date is today or earlier, or within 2 days. */
  paymentsDueSoon: z.number().int().default(0),
  /** Pending items across every open case (QUEUED / ASSIGNED / IN_PROGRESS). */
  pending: z.object({
    flight: z.number().int(),
    hotel: z.number().int(),
    activity: z.number().int(),
    transfer: z.number().int(),
    cruise: z.number().int().default(0),
    visa: z.number().int().default(0),
    insurance: z.number().int().default(0),
    meal: z.number().int().default(0),
    fixed_departure: z.number().int().default(0),
    other: z.number().int(),
  }),
});
export type OpsCaseSummary = z.infer<typeof OpsCaseSummarySchema>;

export const OpsAssigneeSchema = z.object({
  id: z.string().uuid(),
  fullName: z.string(),
  role: z.string(),
  opsDepartment: OpsDepartmentSchema.nullable().default(null),
  online: z.boolean(),
  openCases: z.number().int(),
});
export type OpsAssignee = z.infer<typeof OpsAssigneeSchema>;
export const OpsAssigneeListSchema = z.array(OpsAssigneeSchema);

// ---------------------------------------------------------------------------
// Per-item booking state (hold / issue) - one per itinerary component.
// ---------------------------------------------------------------------------

export const OPS_CASE_ITEM_STATUSES = [
  'PENDING',
  'HOLD',
  'APPROVAL_PENDING',
  'APPROVAL_REJECTED',
  'FUND_REQUESTED',
  'FUND_PAID',
  'ISSUED',
  'AMENDMENT_APPROVAL',
  'AMENDMENT',
] as const;

/** Lead-wide price increase (INR, over the quote) that ops may absorb without approval. */
export const OPS_PRICE_INCREASE_LIMIT_INR = 500;
export const OpsCaseItemStatusSchema = z.enum(OPS_CASE_ITEM_STATUSES);
export type OpsCaseItemStatus = z.infer<typeof OpsCaseItemStatusSchema>;

export const OPS_QC_CURRENCIES = [
  'INR',
  'USD',
  'EUR',
  'GBP',
  'AED',
  'SGD',
  'THB',
  'MYR',
  'IDR',
  'VND',
  'LKR',
] as const;

// ---------------------------------------------------------------------------
// Booking details: what ops needs in front of them to book one item, pulled
// from the itinerary component, its master (hotel / activity / transfer /
// cruise / visa / insurance / meal) and the supplier.
// ---------------------------------------------------------------------------

const str = z.string().nullable();
const num = z.number().nullable();

export const OpsSupplierInfoSchema = z.object({
  name: z.string(),
  contactName: str,
  contactPhone: str,
  contactEmail: str,
  contractRef: str,
});

export const OpsFlightSegmentSchema = z.object({
  origin: str,
  destination: str,
  originCity: str,
  destinationCity: str,
  originAirport: str,
  destinationAirport: str,
  originTerminal: str,
  destinationTerminal: str,
  airline: str,
  airlineName: str,
  flightNumber: str,
  /** ISO local datetime as the supplier gave it, e.g. 2026-11-21T12:20:00 */
  departureAt: str,
  arrivalAt: str,
  cabinClass: str,
  baggageAllowance: str,
  cabinBaggage: str,
  durationMinutes: num,
});
export type OpsFlightSegment = z.infer<typeof OpsFlightSegmentSchema>;

export const OpsHotelRoomSchema = z.object({
  name: str,
  adults: z.number().int(),
  children: z.number().int(),
  childAges: z.array(z.number().int()),
  mealType: str,
});

/**
 * What a fixed departure package carries, shown on its land-package item so
 * the ground desk sees the whole template at once: the stays with their
 * occupancy and meal plans, any flights (booked by the Flight desk, listed
 * here for context), the selected supplements and the master's inclusions.
 */
export const OpsFdHotelSchema = z.object({
  name: z.string(),
  city: str,
  checkIn: str,
  checkOut: str,
  nights: num,
  occupancy: str,
  mealPlan: str,
});
export type OpsFdHotel = z.infer<typeof OpsFdHotelSchema>;

export const OpsFdFlightSchema = z.object({
  title: z.string(),
  /** Held block inventory from the template, as opposed to a live TBO pick. */
  block: z.boolean(),
  date: str,
  segments: z.array(OpsFlightSegmentSchema),
});
export type OpsFdFlight = z.infer<typeof OpsFdFlightSchema>;

export const OpsFdPackageSchema = z.object({
  packageTitle: str,
  reference: str,
  categoryName: str,
  supplierName: str,
  nights: num,
  days: num,
  hotels: z.array(OpsFdHotelSchema),
  flights: z.array(OpsFdFlightSchema),
  supplements: z.array(z.object({ title: z.string(), kind: str })),
  inclusions: z.array(z.string()),
});
export type OpsFdPackage = z.infer<typeof OpsFdPackageSchema>;

/**
 * A fixed departure's chosen supplement (vehicle upgrade, visa, insurance,
 * "other" add-on). It is a trip-wide extra priced on the package, not a
 * dated service: no route, day or pickup time exists for it.
 */
export const OpsFdSupplementSchema = z.object({
  kind: str,
  label: z.string(),
  /** From the master: what triggers the charge ("If pickup from Jammu"). */
  condition: str,
  vehicleType: str,
  /** Units chosen for a per-vehicle (PRIVATE) supplement; null when per-pax. */
  quantity: num,
  packageTitle: str,
  packageReference: str,
  travelStart: str,
  travelEnd: str,
});
export type OpsFdSupplement = z.infer<typeof OpsFdSupplementSchema>;

export const OpsBookingDetailsSchema = z.object({
  supplier: OpsSupplierInfoSchema.nullable(),
  /** Pax this item is for (from the component; falls back to the trip). */
  pax: z.object({
    adults: z.number().int(),
    children: z.number().int(),
    infants: z.number().int(),
    childAges: z.array(z.number().int()),
  }),
  startTime: str,
  endTime: str,
  pnr: str,
  supplierRef: str,
  flight: z
    .object({
      airlineName: str,
      outbound: z.array(OpsFlightSegmentSchema),
      returns: z.array(OpsFlightSegmentSchema),
    })
    .nullable(),
  hotel: z
    .object({
      hotelName: str,
      starRating: num,
      address: str,
      city: str,
      checkIn: str,
      checkOut: str,
      nights: num,
      roomName: str,
      roomType: str,
      mealPlan: str,
      occupancy: str,
      rooms: z.array(OpsHotelRoomSchema),
      checkInTime: str,
      checkOutTime: str,
      tboHotelCode: str,
      tboBookingCode: str,
    })
    .nullable(),
  transfer: z
    .object({
      fromLocation: str,
      toLocation: str,
      /** The day's city - what a "within city" transfer with no endpoints is about. */
      cityName: str,
      pickupTime: str,
      mode: str,
      serviceType: str,
      category: str,
      transferType: str,
      vehicleType: str,
      vehicleCount: num,
      operatorName: str,
      vesselOrServiceName: str,
      durationMinutes: num,
    })
    .nullable(),
  activity: z
    .object({
      activityName: str,
      variantName: str,
      category: str,
      pickupPoint: str,
      dropOffPoint: str,
      pickupTime: str,
      durationMinutes: num,
      transferMode: str,
      transferType: str,
      transferDirection: str,
    })
    .nullable(),
  cruise: z
    .object({
      shipName: str,
      cruiseLine: str,
      cabinName: str,
      deck: str,
      nights: num,
      checkIn: str,
      checkOut: str,
      boardBasis: str,
      embarkTime: str,
      disembarkTime: str,
      port: str,
    })
    .nullable(),
  visa: z
    .object({
      visaName: str,
      visaType: str,
      entryType: str,
      processingMode: str,
      validityMonths: num,
      maxStayDays: num,
      targetCountry: str,
    })
    .nullable(),
  insurance: z
    .object({
      name: str,
      insuranceType: str,
      coverageScope: str,
      coverageAmountLabel: str,
      maxCoverageDays: num,
      targetCountry: str,
    })
    .nullable(),
  meal: z
    .object({
      name: str,
      mealKind: str,
      restaurantName: str,
      locationName: str,
      cuisine: str,
      seatingType: str,
    })
    .nullable(),
  /** Only on a fixed departure's land-package item. */
  fixedDeparture: OpsFdPackageSchema.nullable().default(null),
  /** Only on a fixed departure's supplement item (transfer or VAS). */
  fdSupplement: OpsFdSupplementSchema.nullable().default(null),
});
export type OpsBookingDetails = z.infer<typeof OpsBookingDetailsSchema>;

/** Why a fund request waits for an Admin. */
export const OpsApprovalReasonSchema = z.enum(['PRICE_INCREASE', 'OVER_RECEIPTS']);
export type OpsApprovalReason = z.infer<typeof OpsApprovalReasonSchema>;

/**
 * The lead's money as Accounts needs it next to a supplier payment (INR):
 * what the customer agreed to pay, has paid and still owes; and of what was
 * paid, how much is already committed to / spent on suppliers.
 */
export const OpsLeadMoneySchema = z.object({
  /** The won price (`Lead.saleAmount`); null when never recorded. */
  saleInr: z.number().nullable(),
  /** Paid by the customer so far (paid instalments on the lead's payment plans). */
  receivedInr: z.number(),
  /** sale - received; null without a sale price. */
  balanceInr: z.number().nullable(),
  /** Supplier payments asked for across the lead (requested, approved-pending, paid, issued). */
  committedInr: z.number(),
  /** Of that, actually paid to suppliers. */
  utilisedInr: z.number(),
  /** received - utilised: what is still in hand to pay suppliers with. */
  availableInr: z.number(),
});
export type OpsLeadMoney = z.infer<typeof OpsLeadMoneySchema>;

/** One instalment as ops and Accounts see it. */
export const OpsInstalmentSchema = z.object({
  id: z.string().uuid(),
  seq: z.number().int(),
  amount: z.number(),
  currency: z.string(),
  /** Pay-by (one day before the supplier's deadline). */
  dueDate: z.coerce.date(),
  label: z.string().nullable(),
  paidAt: z.coerce.date().nullable(),
  paidByName: z.string().nullable(),
  paymentReference: z.string().nullable(),
  hasPaymentProof: z.boolean(),
});
export type OpsInstalment = z.infer<typeof OpsInstalmentSchema>;

export const OpsCaseItemSchema = z.object({
  componentId: z.string().uuid(),
  /**
   * The desk case this item is booked under. Managers and the lead's
   * Operations owner see every desk's items on one case page, so an action
   * must go to the item's own case, not the page's.
   */
  caseId: z.string().uuid(),
  department: OpsDepartmentSchema,
  kind: z.enum(['FLIGHT', 'HOTEL', 'TRANSFER', 'ACTIVITY', 'VAS', 'CRUISE']),
  /** Set when kind is VAS and the component points at a visa / insurance / meal master. */
  /** An unknown kind (older bundle vs newer API) reads as `other` instead of breaking the page. */
  vasKind: OpsVasKindSchema.nullable().default(null).catch(null),
  title: z.string(),
  dayIndex: z.number().int().nullable(),
  startDate: z.coerce.date().nullable(),
  endDate: z.coerce.date().nullable(),
  fromLocation: z.string().nullable(),
  toLocation: z.string().nullable(),
  /** Quoted cost on the sold itinerary, in the itinerary currency. */
  quotedCost: z.number(),
  quotedCurrency: z.string(),
  /**
   * Included in a package (quoted at 0, e.g. the hotel nights inside a fixed
   * departure's land package): nothing to book or pay on its own, so no
   * actions and not counted as work. Flights are never bundled - an FD's
   * flight is booked separately.
   */
  bundled: z.boolean().default(false),
  status: OpsCaseItemStatusSchema,
  qcPrice: z.number().nullable(),
  currency: z.string(),
  holdReference: z.string().nullable(),
  paymentDueDate: z.coerce.date().nullable(),
  issuedReference: z.string().nullable(),
  note: z.string().nullable(),
  heldAt: z.coerce.date().nullable(),
  heldByName: z.string().nullable(),
  issuedAt: z.coerce.date().nullable(),
  issuedByName: z.string().nullable(),
  // Fund request / approval / payment / amendment.
  supplierName: z.string().nullable().default(null),
  fundAmount: z.number().nullable().default(null),
  fundCurrency: z.string().nullable().default(null),
  priceDeltaInr: z.number().nullable().default(null),
  fundRequestedAt: z.coerce.date().nullable().default(null),
  approvalRequired: z.boolean().default(false),
  approvalIncreaseInr: z.number().nullable().default(null),
  approvalApproved: z.boolean().nullable().default(null),
  approvalNote: z.string().nullable().default(null),
  approvalDecidedByName: z.string().nullable().default(null),
  approvalReason: OpsApprovalReasonSchema.nullable().default(null),
  /** INR asked for the lead beyond what the customer has paid (OVER_RECEIPTS). */
  approvalShortfallInr: z.number().nullable().default(null),
  paidAt: z.coerce.date().nullable().default(null),
  paymentReference: z.string().nullable().default(null),
  hasPaymentProof: z.boolean().default(false),
  amendmentReason: z.string().nullable().default(null),
  amendmentRequestedAt: z.coerce.date().nullable().default(null),
  /** Supplier payment plan, in order. Empty until a hold with instalments or a fund request. */
  instalments: z.array(OpsInstalmentSchema).default([]),
  /**
   * Round-trip flights booked as ONE ticket: 'outbound' carries the actions
   * and the state, 'return' mirrors it (no buttons of its own). Null for
   * everything else.
   */
  bundleRole: z.enum(['outbound', 'return']).nullable().default(null),
  /** Booking facts for the full-page booking screen. */
  details: OpsBookingDetailsSchema.nullable().default(null),
});
export type OpsCaseItem = z.infer<typeof OpsCaseItemSchema>;
export const OpsCaseItemListSchema = z.array(OpsCaseItemSchema);

/** One traveller as the customer uploaded them (passport / PAN file). */
export const OpsTravellerSchema = z.object({
  /** traveller_document_files id - opens the scan via /traveller-documents/files/:id/download. */
  fileId: z.string().uuid().nullable().default(null),
  type: z.enum(['PAN', 'PASSPORT']),
  travellerName: str,
  fileName: z.string(),
  uploadedAt: z.coerce.date(),
  rejected: z.boolean(),
});

/** Everything the full-page booking screen needs in one call. */
export const OpsBookingSheetSchema = z.object({
  case: OpsCaseRowSchema,
  trip: z.object({
    title: z.string(),
    status: z.string(),
    kind: z.string(),
    fromCity: str,
    travelStartDate: z.coerce.date().nullable(),
    travelEndDate: z.coerce.date().nullable(),
    numberOfNights: z.number().int(),
    numberOfDays: z.number().int(),
    adults: z.number().int(),
    children: z.number().int(),
    infants: z.number().int(),
    childAges: z.array(z.number().int()),
    roomsCount: z.number().int(),
    hotelCategory: str,
    currency: z.string(),
    grandTotal: z.number(),
  }),
  customer: z.object({
    name: z.string(),
    email: str,
    mobile: str,
  }),
  /** The lead's money: paid by the customer vs committed to suppliers. */
  money: OpsLeadMoneySchema.nullable().default(null),
  /** The latest live contract on the lead (not declined / voided); null when none was ever made. */
  contract: z
    .object({
      id: z.string().uuid(),
      status: ContractStatusSchema,
      sentAt: z.coerce.date().nullable(),
      completedAt: z.coerce.date().nullable(),
    })
    .nullable()
    .default(null),
  documents: z.object({
    /** The traveller document request behind the files; ops verifies / uploads against it. */
    requestId: z.string().uuid().nullable().default(null),
    status: TravellerDocumentRequestStatusSchema.nullable(),
    travellers: z.array(OpsTravellerSchema),
  }),
  items: z.array(OpsCaseItemSchema),
});
export type OpsBookingSheet = z.infer<typeof OpsBookingSheetSchema>;
export type OpsTraveller = z.infer<typeof OpsTravellerSchema>;

const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');

/** Hold: all four facts are required. */
/** Most instalments a supplier payment can be split into. */
export const OPS_INSTALMENTS_MAX = 6;

/** One planned supplier payment: amount and the supplier's last date for it. */
export const OpsInstalmentInputSchema = z.object({
  amount: z.coerce.number().positive('Enter the instalment amount'),
  /** The supplier's deadline; stored one day earlier as the pay-by, like the hold's. */
  dueDate: isoDay,
  label: z.string().trim().max(60).optional(),
});
export type OpsInstalmentInput = z.infer<typeof OpsInstalmentInputSchema>;

export const OpsHoldItemInputSchema = z
  .object({
    qcPrice: z.coerce.number().positive('QC price must be more than 0'),
    currency: z.enum(OPS_QC_CURRENCIES).default('INR'),
    /** Supplier the hold is with (supplier master name); prefilled on the fund request. */
    supplierName: z.string().trim().max(160).optional(),
    holdReference: z.string().trim().min(1, 'Holding number is required').max(120),
    paymentDueDate: isoDay,
    note: z.string().trim().max(1000).optional(),
    /**
     * How the supplier is paid (2026-10-28): several instalments adding up to
     * the QC price, each with its own deadline. Omitted / empty = one full
     * payment by `paymentDueDate`.
     */
    instalments: z.array(OpsInstalmentInputSchema).max(OPS_INSTALMENTS_MAX).optional(),
  })
  .superRefine((v, ctx) => {
    if (!v.instalments?.length) return;
    const total = v.instalments.reduce((s, i) => s + i.amount, 0);
    if (Math.abs(total - v.qcPrice) > 0.01) {
      ctx.addIssue({
        code: 'custom',
        path: ['instalments'],
        message: 'The instalments must add up to the QC price',
      });
    }
  });
export type OpsHoldItemInput = z.infer<typeof OpsHoldItemInputSchema>;

/** Issue: only after the supplier was paid; the confirmation number is required. */
// ---------------------------------------------------------------------------
// Documents on a case: payment proofs, tickets, vouchers. INTERNAL stays with
// ops / accounts, CUSTOMER is what gets sent on to the traveller.
// ---------------------------------------------------------------------------

export const OPS_DOCUMENT_KINDS = [
  'PAYMENT_PROOF',
  'TICKET',
  'VOUCHER',
  'INVOICE',
  'OTHER',
] as const;
export const OpsDocumentKindSchema = z.enum(OPS_DOCUMENT_KINDS);
export type OpsDocumentKind = z.infer<typeof OpsDocumentKindSchema>;

export const OPS_DOCUMENT_AUDIENCES = ['INTERNAL', 'CUSTOMER'] as const;
export const OpsDocumentAudienceSchema = z.enum(OPS_DOCUMENT_AUDIENCES);
export type OpsDocumentAudience = z.infer<typeof OpsDocumentAudienceSchema>;

export const OPS_DOCUMENT_MAX_BYTES = 8 * 1024 * 1024;
/** How many files one issue call may attach. */
export const OPS_ISSUE_MAX_DOCUMENTS = 5;

/** One file as uploaded from the admin: bare base64, PNG / JPG / WebP / PDF. */
export const OpsDocumentFileInputSchema = z.object({
  fileName: z.string().trim().min(1).max(200),
  contentBase64: z.string().min(1, 'Choose a file'),
  kind: OpsDocumentKindSchema.exclude(['PAYMENT_PROOF']).default('TICKET'),
  audience: OpsDocumentAudienceSchema.default('CUSTOMER'),
  note: z.string().trim().max(500).optional(),
});
export type OpsDocumentFileInput = z.infer<typeof OpsDocumentFileInputSchema>;

/** Hand upload on the case, optionally pinned to one booking item. */
export const OpsDocumentUploadInputSchema = OpsDocumentFileInputSchema.extend({
  componentId: z.string().uuid().optional(),
});
export type OpsDocumentUploadInput = z.infer<typeof OpsDocumentUploadInputSchema>;

export const OpsCaseDocumentSchema = z.object({
  id: z.string().uuid(),
  opsCaseId: z.string().uuid(),
  componentId: z.string().uuid().nullable(),
  componentTitle: z.string().nullable(),
  kind: OpsDocumentKindSchema,
  audience: OpsDocumentAudienceSchema,
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  note: z.string().nullable(),
  uploadedById: z.string().uuid(),
  uploadedByName: z.string(),
  uploadedByRole: z.string(),
  createdAt: z.coerce.date(),
  /** The viewer may remove it (uploader or an ops manager; never a payment proof). */
  canDelete: z.boolean(),
});
export type OpsCaseDocument = z.infer<typeof OpsCaseDocumentSchema>;
export const OpsCaseDocumentListSchema = z.array(OpsCaseDocumentSchema);

export const OpsIssueItemInputSchema = z
  .object({
    issuedReference: z.string().trim().min(1, 'Confirmation number is required').max(120),
    qcPrice: z.coerce.number().positive().optional(),
    currency: z.enum(OPS_QC_CURRENCIES).optional(),
    note: z.string().trim().max(1000).optional(),
    /** Ticket / voucher files to attach while issuing (default: TICKET for the customer). */
    documents: z.array(OpsDocumentFileInputSchema).max(OPS_ISSUE_MAX_DOCUMENTS).optional(),
  })
  // At least one file (2026-10-27): an item issued without one left the
  // customer with nothing to receive. Kept off the field type so server-side
  // callers (tests) can still issue directly; the HTTP body is checked here.
  .superRefine((v, ctx) => {
    if (!v.documents?.length) {
      ctx.addIssue({ code: 'custom', path: ['documents'], message: 'Attach the ticket / voucher' });
    }
  });
export type OpsIssueItemInput = z.infer<typeof OpsIssueItemInputSchema>;

// ---------------------------------------------------------------------------
// Fund flow: ask for fund -> (approval) -> accounts pays -> ops issues.
// ---------------------------------------------------------------------------

/** Ops asks Accounts to pay the supplier for one item. */
export const OpsRequestFundInputSchema = z.object({
  amount: z.coerce.number().positive('Enter the amount to pay the supplier'),
  currency: z.enum(OPS_QC_CURRENCIES).default('INR'),
  supplierName: z.string().trim().min(1, 'Supplier name is required').max(160),
  note: z.string().trim().max(1000).optional(),
});
export type OpsRequestFundInput = z.infer<typeof OpsRequestFundInputSchema>;

export const OpsApprovalDecisionInputSchema = z.object({
  approve: z.boolean(),
  note: z.string().trim().max(1000).optional(),
});
export type OpsApprovalDecisionInput = z.infer<typeof OpsApprovalDecisionInputSchema>;

export const OPS_PAYMENT_PROOF_MAX_BYTES = 8 * 1024 * 1024;

/** Accounts marks the supplier paid, with the payment screenshot. */
export const OpsMarkPaidInputSchema = z.object({
  /** Which instalment this payment is for; omitted = the next unpaid one. */
  instalmentId: z.string().uuid().optional(),
  /** UTR / transaction reference - required (2026-10-27), it is what reconciles the payment. */
  paymentReference: z.string().trim().min(1, 'Enter the payment reference / UTR').max(160),
  proofFileName: z.string().trim().min(1).max(200),
  /** Bare base64 (no data: prefix). PNG / JPG / WebP / PDF. */
  proofBase64: z.string().min(1, 'Upload the payment screenshot'),
});
export type OpsMarkPaidInput = z.infer<typeof OpsMarkPaidInputSchema>;

/** Not available: send the item back to the seller. */
export const OpsAmendmentInputSchema = z.object({
  reason: z.string().trim().min(1, 'Say what is not available').max(1000),
});
export type OpsAmendmentInput = z.infer<typeof OpsAmendmentInputSchema>;

export const OpsResolveAmendmentInputSchema = z.object({
  note: z.string().trim().max(1000).optional(),
});
export type OpsResolveAmendmentInput = z.infer<typeof OpsResolveAmendmentInputSchema>;

/** One queue row for Accounts, Approvers or the Seller (same shape). */
export const OpsItemQueueRowSchema = z.object({
  opsCaseId: z.string().uuid(),
  componentId: z.string().uuid(),
  status: OpsCaseItemStatusSchema,
  leadId: z.string().uuid(),
  leadReference: z.string(),
  customerName: z.string(),
  tripTitle: z.string().nullable(),
  kind: z.enum(['FLIGHT', 'HOTEL', 'TRANSFER', 'ACTIVITY', 'VAS', 'CRUISE']),
  /** An unknown kind (older bundle vs newer API) reads as `other` instead of breaking the page. */
  vasKind: OpsVasKindSchema.nullable().default(null).catch(null),
  itemTitle: z.string(),
  startDate: z.coerce.date().nullable(),
  supplierName: z.string().nullable(),
  fundAmount: z.number().nullable(),
  fundCurrency: z.string().nullable(),
  quotedInr: z.number().nullable(),
  fundInr: z.number().nullable(),
  priceDeltaInr: z.number().nullable(),
  approvalIncreaseInr: z.number().nullable(),
  paymentDueDate: z.coerce.date().nullable(),
  holdReference: z.string().nullable(),
  note: z.string().nullable(),
  requestedByName: z.string().nullable(),
  fundRequestedAt: z.coerce.date().nullable(),
  paidAt: z.coerce.date().nullable(),
  paidByName: z.string().nullable(),
  paymentReference: z.string().nullable(),
  hasPaymentProof: z.boolean(),
  amendmentReason: z.string().nullable(),
  amendmentRequestedAt: z.coerce.date().nullable(),
  opsOwnerName: z.string().nullable(),
  approvalReason: OpsApprovalReasonSchema.nullable().default(null),
  approvalShortfallInr: z.number().nullable().default(null),
  /** The lead's money next to this payment, so Accounts sees what is in hand. */
  leadMoney: OpsLeadMoneySchema.nullable().default(null),
  /**
   * The instalment this row is about (one queue row per instalment), with
   * where the plan stands. Null for items paid before plans existed.
   */
  instalment: OpsInstalmentSchema.extend({
    count: z.number().int(),
    paidCount: z.number().int(),
  })
    .nullable()
    .default(null),
});
export type OpsItemQueueRow = z.infer<typeof OpsItemQueueRowSchema>;
export const OpsItemQueueSchema = z.array(OpsItemQueueRowSchema);

export const OpsProofUrlSchema = z.object({ url: z.string(), expiresAt: z.coerce.date() });
export type OpsProofUrl = z.infer<typeof OpsProofUrlSchema>;
